import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import InvoicesPage from "@/pages/InvoicesPage";
import CreateInvoicePage from "@/pages/CreateInvoicePage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerGroupsPage from "@/pages/CustomerGroupsPage";
import ServiceGroupsPage from "@/pages/ServiceGroupsPage";
import ServicesPage from "@/pages/ServicesPage";
import ReportsPage from "@/pages/ReportsPage";
import StaffPage from "@/pages/StaffPage";
import ShiftSettingsPage from "@/pages/ShiftSettingsPage";
import StaffPermissionsPage from "@/pages/StaffPermissionsPage";
import AttendancePage from "@/pages/AttendancePage";
import LeavePage from "@/pages/LeavePage";
import SalaryPage from "@/pages/SalaryPage";
import PerformancePage from "@/pages/PerformancePage";
import WorkLogPage from "@/pages/WorkLogPage";
import StaffReportsPage from "@/pages/StaffReportsPage";
import IncentivesPage from "@/pages/IncentivesPage";
import DailyTransactionPage from "@/pages/DailyTransactionPage";
import SettingsPage from "@/pages/SettingsPage";
import EditInvoicePage from "@/pages/EditInvoicePage";
import ProcessingPage from "@/pages/ProcessingPage";
import RecurringPage from "@/pages/RecurringPage";
import MyWorkPage from "@/pages/MyWorkPage";
import TransferPage from "@/pages/TransferPage";
import ReceiptPage from "@/pages/ReceiptPage";
import RefundPage from "@/pages/RefundPage";
import TransactionPage from "@/pages/TransactionPage";
import NotesPage from "@/pages/NotesPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import SignUpPage from "@/pages/SignUpPage";
import BillingPage from "@/pages/BillingPage";
import PublicPayPage from "@/pages/PublicPayPage";
import { AboutPage, ContactPage, FAQPage, PrivacyPage, TermsPage, RefundPage as MarketingRefundPage, DemoRequestPage, FeaturesPage, PricingPage, QuickStartGuidePage } from "@/pages/MarketingPages";
import StaffRouteGuard from "@/components/StaffRouteGuard";

function Loading() {
  return (
    <div className="h-screen flex items-center justify-center">
      <p className="text-sm text-[#52525B]">Loading...</p>
    </div>
  );
}

function TenantOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/superadmin" replace />;
  return children;
}

function SuperAdminOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const next = params.get('next');
  if (loading) return <Loading />;
  if (user && user.role === 'super_admin') return <Navigate to="/superadmin" replace />;
  if (user) return <Navigate to={next && next.startsWith('/') && !next.startsWith('//') ? next : '/dashboard'} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
            <Route path="/signup" element={<AuthRoute><SignUpPage /></AuthRoute>} />
            {/* Public marketing pages — accessible whether the user is
                logged in or not. Kept above the tenant-only catch-all so
                the Layout guard doesn't intercept them. */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pay/:token" element={<PublicPayPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/quick-start-guide" element={<QuickStartGuidePage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refund-policy" element={<MarketingRefundPage />} />
            <Route path="/demo-request" element={<DemoRequestPage />} />
            <Route path="/free-trial" element={<Navigate to="/signup" replace />} />
            <Route path="/superadmin" element={<SuperAdminOnlyRoute><SuperAdminPage /></SuperAdminOnlyRoute>} />
            <Route path="/" element={<TenantOnlyRoute><StaffRouteGuard><Layout /></StaffRouteGuard></TenantOnlyRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="invoices/create" element={<CreateInvoicePage />} />
              {/* Alias: keep older bookmarks / dashboard widgets pointing at /invoices/new working. */}
              <Route path="invoices/new" element={<CreateInvoicePage />} />
              <Route path="invoices/edit/:invoiceId" element={<EditInvoicePage />} />
              <Route path="processing" element={<ProcessingPage />} />
              <Route path="recurring" element={<RecurringPage />} />
              <Route path="my-work" element={<MyWorkPage />} />
              <Route path="notes" element={<NotesPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customer-groups" element={<CustomerGroupsPage />} />
              <Route path="service-groups" element={<ServiceGroupsPage />} />
              <Route path="services" element={<ServicesPage />} />
              <Route path="finance" element={<Navigate to="/reports" replace />} />
              <Route path="transfer" element={<TransferPage />} />
              <Route path="receipt" element={<ReceiptPage />} />
              <Route path="refund" element={<RefundPage />} />
              <Route path="expense" element={<TransactionPage type="expense" />} />
              <Route path="income" element={<TransactionPage type="income" />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/daily" element={<DailyTransactionPage />} />
              <Route path="staff" element={<StaffPage />} />
              <Route path="staff/attendance" element={<AttendancePage />} />
              <Route path="staff/leave" element={<LeavePage />} />
              <Route path="staff/salary" element={<SalaryPage />} />
              <Route path="staff/performance" element={<PerformancePage />} />
              <Route path="staff/work-log" element={<WorkLogPage />} />
              <Route path="staff/incentives" element={<IncentivesPage />} />
              <Route path="staff/reports" element={<StaffReportsPage />} />
              <Route path="staff/shifts" element={<ShiftSettingsPage />} />
              <Route path="staff/permissions" element={<StaffPermissionsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="billing" element={<BillingPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
